import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Calendar } from 'primereact/calendar'
import { useTranslation } from '../i18n'
import { useApi, apiPatch } from '../hooks/useApi'
import { getAuthToken } from '../context/AuthContext'
import {
  ProjectList,
  BudgetSummary,
  TestList,
  TestBrowser,
  TestMaintenanceSection,
  FTESettings,
  GlobalAvgEffort,
  UnassignedTests,
  StaleTests,
  EffortEntries,
} from '../components'
import type { Project, ProjectBudget, Test, Settings } from '../types'

export function Configuration() {
  const { t } = useTranslation()
  const { projectId } = useParams<{ projectId?: string }>()
  const projects = useApi<Project[]>('/projects')
  const settings = useApi<Settings>('/settings')
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const handleDownloadData = async () => {
    setDownloading(true)
    setDownloadError(null)
    try {
      const token = getAuthToken()
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      const response = await fetch('/api/data/download', { headers })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const blob = await response.blob()
      const disposition = response.headers.get('Content-Disposition')
      const filenameMatch = disposition?.match(/filename="(.+)"/)
      const filename = filenameMatch?.[1] ?? 'data-backup.tar.gz'
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      setDownloadError(t('error.failedToDownloadData'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div data-testid="configuration-page">
      <h1 className="page-title">{t('page.configuration')}</h1>

      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={handleDownloadData}
          className="btn btn-primary"
          disabled={downloading}
          data-testid="download-data-button"
          aria-label={t('button.downloadData')}
          title={t('button.downloadData')}
        >
          <i className="pi pi-download" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
          {downloading ? t('misc.downloading') : t('button.downloadData')}
        </button>
        {downloadError && (
          <p
            style={{ color: 'var(--color-error)', marginTop: '0.5rem', fontSize: '0.875rem' }}
            data-testid="download-error"
          >
            {downloadError}
          </p>
        )}
      </div>

      <FTESettings settings={settings.data} onUpdate={settings.refetch} />

      <GlobalAvgEffort />

      <EffortEntries />

      <TestMaintenanceSection />

      <UnassignedTests />

      <StaleTests />

      <div className="grid grid-cols-3" style={{ marginTop: '1.5rem' }}>
        <ProjectList
          projects={projects.data ?? []}
          loading={projects.loading}
          selectedId={projectId}
          onProjectCreated={projects.refetch}
        />

        <div className="card" style={{ gridColumn: 'span 2' }}>
          {projectId ? (
            <ProjectDetail projectId={projectId} />
          ) : (
            <div
              style={{
                color: 'var(--color-gray-400)',
                textAlign: 'center',
                padding: '4rem',
              }}
              data-testid="no-project-selected"
            >
              {t('empty.selectProject')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ProjectDetail({ projectId }: { projectId: string }) {
  const { t } = useTranslation()
  const [showTestBrowser, setShowTestBrowser] = useState(false)
  const project = useApi<Project>(`/projects/${projectId}`)
  const budget = useApi<ProjectBudget>(`/projects/${projectId}/budget`)
  const allTests = useApi<Test[]>('/tests')

  const projectTests = allTests.data?.filter((t) => t.projectIds.includes(projectId)) ?? []

  const handleTestUpdated = () => {
    budget.refetch()
    allTests.refetch()
  }

  const handleProjectUpdated = () => {
    project.refetch()
    budget.refetch()
  }

  const handleTestsAssigned = () => {
    budget.refetch()
    allTests.refetch()
  }

  if (project.loading) {
    return (
      <div data-testid="project-detail" style={{ padding: '2rem', textAlign: 'center' }}>
        {t('misc.loadingProject')}
      </div>
    )
  }

  if (project.error || !project.data) {
    return (
      <div
        data-testid="project-detail"
        style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-error)' }}
      >
        {t('error.projectNotFound')}
      </div>
    )
  }

  return (
    <div data-testid="project-detail">
      <h2 className="card-title">{project.data.name}</h2>
      {project.data.description && (
        <p style={{ color: 'var(--color-gray-400)', marginBottom: '1.5rem' }}>
          {project.data.description}
        </p>
      )}

      <StartDateEditor project={project.data} onUpdate={handleProjectUpdated} />

      <BudgetSummary budget={budget.data} loading={budget.loading} />

      <TestList
        tests={projectTests}
        loading={allTests.loading}
        projectId={projectId}
        onTestUpdated={handleTestUpdated}
      />

      {!showTestBrowser && (
        <button
          onClick={() => setShowTestBrowser(true)}
          className="btn btn-primary"
          style={{ marginTop: '1rem' }}
          data-testid="add-tests-button"
          aria-label={t('button.addTests')}
          title={t('button.addTests')}
        >
          <i className="pi pi-plus" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
          {t('button.addTests')}
        </button>
      )}

      {showTestBrowser && (
        <TestBrowser
          projectId={projectId}
          onTestsAssigned={handleTestsAssigned}
          onClose={() => setShowTestBrowser(false)}
        />
      )}
    </div>
  )
}

function StartDateEditor({ project, onUpdate }: { project: Project; onUpdate: () => void }) {
  const { t } = useTranslation()
  const [startDate, setStartDate] = useState<Date | null>(
    project.startDate ? new Date(project.startDate) : null
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      await apiPatch<Project>(`/projects/${project.id}`, {
        startDate: startDate ? startDate.toISOString() : null,
      })
      onUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToUpdateStartDate'))
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    setStartDate(null)
    setSaving(true)
    setError(null)

    try {
      await apiPatch<Project>(`/projects/${project.id}`, { startDate: null })
      onUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToClearStartDate'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'var(--color-surface-secondary)',
        borderRadius: '4px',
      }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="startDate" style={{ fontWeight: 500 }}>
          {t('label.startDate')}
        </label>
        <Calendar
          id="startDate"
          value={startDate}
          onChange={(e) => setStartDate(e.value as Date | null)}
          dateFormat="yy-mm-dd"
          showIcon
          data-testid="start-date-input"
          pt={{
            root: { style: { display: 'inline-flex' } },
            input: {
              root: {
                style: {
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '0.375rem 0 0 0.375rem',
                  padding: '0.5rem 0.75rem',
                },
              },
            },
            dropdownButton: {
              root: {
                style: {
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  border: '1px solid var(--color-border)',
                  borderLeft: 'none',
                  borderRadius: '0 0.375rem 0.375rem 0',
                },
              },
            },
            panel: {
              style: {
                background: 'var(--color-surface)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              },
            },
            header: {
              style: {
                background: 'var(--color-surface)',
                color: 'var(--color-text)',
                borderBottom: '1px solid var(--color-border)',
                padding: '0.5rem',
              },
            },
            title: { style: { color: 'var(--color-text)' } },
            dayLabel: { style: { color: 'var(--color-text-secondary)' } },
            day: { style: { color: 'var(--color-text)' } },
            monthTitle: {
              style: { color: 'var(--color-text)', background: 'transparent', border: 'none' },
            },
            yearTitle: {
              style: { color: 'var(--color-text)', background: 'transparent', border: 'none' },
            },
            previousButton: { style: { color: 'var(--color-text)' } },
            nextButton: { style: { color: 'var(--color-text)' } },
          }}
        />
        <button
          onClick={handleSave}
          className="btn btn-primary btn-sm"
          disabled={saving}
          data-testid="start-date-save"
          aria-label={t('button.save')}
          title={t('button.save')}
        >
          <i className="pi pi-check" aria-hidden="true" style={{ marginRight: '0.25rem' }} />
          {t('button.save')}
        </button>
        {startDate && (
          <button
            onClick={handleClear}
            className="btn btn-danger btn-sm"
            disabled={saving}
            data-testid="start-date-clear"
            aria-label={t('button.clear')}
            title={t('button.clear')}
          >
            <i className="pi pi-times" aria-hidden="true" style={{ marginRight: '0.25rem' }} />
            {t('button.clear')}
          </button>
        )}
      </div>
      {error && (
        <p style={{ color: 'var(--color-error)', marginTop: '0.5rem', fontSize: '0.875rem' }}>
          {error}
        </p>
      )}
    </div>
  )
}
